import { Button } from '@components'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { ComponentPropsWithoutRef } from 'react'
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next'
import styles from './StripePayment.module.scss'
import { useValidateOrder } from '../../../../../vendure-plugin-configs/vendure-validate-order-plugin'

const TRAP_STYLES_IDS = {
  injected: 'stripe-payment-trap-injected-css',
  components: 'stripe-payment-trap-components-styles',
  ec: 'stripe-payment-trap-ec-styles',
} as const
const TRAP_Z_INDEX = 2147483647
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
type FormSubmitEvent = Parameters<NonNullable<ComponentPropsWithoutRef<'form'>['onSubmit']>>[0]

interface StripeIframeHandlerProps {
  clientSecret: string
  publishableKey: string
  orderCode: string
  onCancel: () => void
}

function CheckoutFormInner({ orderCode, onCancel }: { orderCode: string; onCancel: () => void }) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { validateOrder, error: validateError } = useValidateOrder()

  const handleLoadError = () => {
    setLoadError(t('general.genericErrorMessage'))
  }

  const returnUrl = useMemo(() => {
    const base = t('urls.orderConfirmation')
    return `${window.location.origin}${base}?orderCode=${encodeURIComponent(orderCode)}`
  }, [orderCode, t])

  const handleSubmit = async (event: FormSubmitEvent): Promise<void> => {
    event.preventDefault()
    setErrorMessage(null)

    if (!stripe || !elements) return

    setIsSubmitting(true)

    try {
      const { valid } = await validateOrder()
      if (!valid) return
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
      })

      if (result.error) {
        setErrorMessage(t('general.genericErrorMessage'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const displayError = loadError ?? errorMessage ?? validateError?.message
  const isValidateError = !!validateError?.message

  if (isValidateError) {
    return (
      <div className={styles.form}>
        <div className={styles.error} role="alert" aria-live="assertive">
          <span>{t('checkout.paymentMethod.stripeWindowError')}</span>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            aria-label={t('general.close')}
          >
            {t('general.close')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <PaymentElement onLoadError={handleLoadError} />
      {displayError ? (
        <div className={styles.error} role="alert" aria-live="assertive">
          {displayError}
        </div>
      ) : null}
      <div className={styles.buttonContainer}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('general.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={!stripe || isSubmitting || !!loadError}>
          {isSubmitting ? t('general.processing') : t('general.pay')}
        </Button>
      </div>
    </form>
  )
}

/**
 * Stripe Elements require Light DOM. Instead of moving the iframe into the shadow-root
 * modal (which breaks visibility), keep the trap div in document.body and position it
 * over the placeholder so it visually appears in the modal.
 */
export function StripeIframeHandler({
  clientSecret,
  publishableKey,
  orderCode,
  onCancel,
}: StripeIframeHandlerProps) {
  const { t } = useTranslation()
  const placeholderRef = useRef<HTMLDivElement>(null)
  const idPrefix = useId().replace(/:/g, '')
  const trapId = `${idPrefix}-stripe-payment-trap`
  const placeholderId = `${idPrefix}-stripe-payment-root`
  const stripePromise = useMemo(() => {
    if (!publishableKey) return null
    return loadStripe(publishableKey)
  }, [publishableKey])

  useLayoutEffect(() => {
    if (!clientSecret || !stripePromise || !orderCode) return
    const placeholder = placeholderRef.current
    if (!placeholder) return

    const rootNode = placeholder.getRootNode()
    const shadowRoot = rootNode instanceof ShadowRoot ? rootNode : null
    const styleClones: HTMLStyleElement[] = []
    if (shadowRoot) {
      const styleIds = [
        { id: 'injected-css', cloneId: TRAP_STYLES_IDS.injected },
        { id: 'ecom-components-styles', cloneId: TRAP_STYLES_IDS.components },
        { id: 'ecom-components-ec-styles', cloneId: TRAP_STYLES_IDS.ec },
      ]
      for (const { id, cloneId } of styleIds) {
        const src = shadowRoot.querySelector<HTMLStyleElement>(`#${id}`)
        if (src?.textContent) {
          const clone = document.createElement('style')
          clone.id = cloneId
          clone.textContent = src.textContent
          document.head.appendChild(clone)
          styleClones.push(clone)
        }
      }
    }

    const tmpDiv = document.createElement('div')
    tmpDiv.setAttribute('id', trapId)
    tmpDiv.setAttribute('data-stripe-payment-trap', 'true')
    tmpDiv.setAttribute('role', 'dialog')
    tmpDiv.setAttribute('aria-modal', 'true')
    tmpDiv.setAttribute(
      'aria-label',
      t('completeOrder.completePayment', { defaultValue: 'Complete payment' }),
    )
    tmpDiv.tabIndex = -1
    tmpDiv.style.cssText = `position:fixed;top:0;left:0;width:100%;min-height:480px;visibility:visible;opacity:1;z-index:${TRAP_Z_INDEX};pointer-events:auto;background:#fff;`
    document.body.appendChild(tmpDiv)
    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const stripeForm = (
      <div className={styles.root}>
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutFormInner orderCode={orderCode} onCancel={onCancel} />
        </Elements>
      </div>
    )

    const root = createRoot(tmpDiv)
    root.render(stripeForm)

    const syncPosition = () => {
      const rect = placeholder.getBoundingClientRect()
      tmpDiv.style.top = `${rect.top}px`
      tmpDiv.style.left = `${rect.left}px`
      tmpDiv.style.width = `${rect.width}px`
      tmpDiv.style.minHeight = `${rect.height}px`
      tmpDiv.style.height = `${rect.height}px`
    }

    syncPosition()

    const focusableElements = (): HTMLElement[] =>
      Array.from(tmpDiv.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
        if (element.tabIndex < 0) {
          return false
        }

        return !element.hasAttribute('disabled')
      })

    const moveFocusIntoTrap = () => {
      const [firstFocusable] = focusableElements()
      if (firstFocusable) {
        firstFocusable.focus()
        return
      }

      tmpDiv.focus()
    }

    requestAnimationFrame(() => {
      moveFocusIntoTrap()
    })

    const onTrapKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const elements = focusableElements()
      if (elements.length === 0) {
        event.preventDefault()
        tmpDiv.focus()
        return
      }

      const first = elements[0]
      const last = elements[elements.length - 1]
      const activeElement = document.activeElement

      if (event.shiftKey && activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    tmpDiv.addEventListener('keydown', onTrapKeyDown)

    const resizeObserver = new ResizeObserver(syncPosition)
    resizeObserver.observe(placeholder)

    const handleScroll = () => {
      requestAnimationFrame(syncPosition)
    }
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    window.addEventListener('resize', syncPosition)

    return () => {
      resizeObserver.disconnect()
      document.removeEventListener('scroll', handleScroll, { capture: true })
      window.removeEventListener('resize', syncPosition)
      tmpDiv.removeEventListener('keydown', onTrapKeyDown)
      root.unmount()
      if (tmpDiv.parentNode) {
        tmpDiv.remove()
      }
      styleClones.forEach((el) => el.remove())
      previouslyFocusedElement?.focus()
    }
  }, [clientSecret, stripePromise, orderCode, publishableKey, onCancel, t, trapId])

  if (!stripePromise || !clientSecret) return null

  return <div ref={placeholderRef} id={placeholderId} className={styles.root} aria-hidden="true" />
}
